import { ACTIVITY_TYPE_LABEL } from '@/features/teacher/activities/constants';
import type { FamilyActivity, StudentHomework } from '@/types/student';

/** Which table a piece of work came from — homework, or a class activity. */
export type WorkKind = 'HOMEWORK' | 'ACTIVITY';

/**
 * One piece of work on the student's homework page, whichever table it came
 * from.
 *
 * Homework and class activities are separate tables with separate endpoints,
 * but to the child they are the same job — read it, do it, hand it in — so the
 * list, the cards, the calendar and the details panel draw one shape. The
 * originals ride along in `source` for the actions that differ: a repository
 * link is homework-only, a note to the teacher is activity-only.
 */
export interface StudentWorkItem {
  kind: WorkKind;
  /** `workKey(kind, uuid)` — what the list keys and the upload spinner track. */
  key: string;
  uuid: string;
  title: string;
  subject: { uuid: string; name: string };
  assignedBy: string;
  /** `YYYY-MM-DD`, both. */
  assignedOn: string;
  dueDate: string;
  /** "Lab practical", "Group activity" — null for homework, which has no kind. */
  typeLabel: string | null;
  /** Everything the teacher wrote about what to do, in one block. */
  details: string;
  /** Null when the work is not scored. */
  maxScore: number | null;
  /** "marks" for homework, "points" for an activity — the teacher's own word. */
  scoreUnit: 'marks' | 'points';
  /** Whether the work is handed back as a file or as the address of a repository. */
  submissionFormat: 'FILE' | 'REPO_URL';
  /** The sheet the teacher handed out. Name and size only. */
  attachment: { fileName: string; sizeBytes: number | null } | null;
  submission: {
    handedInOn: string;
    score: number | null;
    feedback: string | null;
    fileName: string | null;
    repoUrl: string | null;
    studentNotes?: string | null;
  } | null;
  source:
    | { kind: 'HOMEWORK'; homework: StudentHomework }
    | { kind: 'ACTIVITY'; activity: FamilyActivity };
}

/** The `key` a piece of work of this kind and uuid carries — unique across both tables. */
export function workKey(kind: WorkKind, uuid: string): string {
  return `${kind}-${uuid}`;
}

export function workFromHomework(homework: StudentHomework): StudentWorkItem {
  return {
    kind: 'HOMEWORK',
    key: workKey('HOMEWORK', homework.uuid),
    uuid: homework.uuid,
    title: homework.title,
    subject: homework.subject,
    assignedBy: homework.assignedBy,
    assignedOn: homework.assignedOn,
    dueDate: homework.dueDate,
    typeLabel: null,
    details: homework.instructions,
    maxScore: homework.maxMarks,
    scoreUnit: 'marks',
    submissionFormat: homework.submissionFormat,
    attachment: homework.attachment,
    submission: homework.submission && {
      handedInOn: homework.submission.handedInOn,
      score: homework.submission.marksObtained,
      feedback: homework.submission.feedback,
      fileName: homework.submission.attachmentName,
      repoUrl: homework.submission.repoUrl,
      studentNotes: homework.submission.studentNotes,
    },
    source: { kind: 'HOMEWORK', homework },
  };
}

export function workFromActivity(activity: FamilyActivity): StudentWorkItem {
  return {
    kind: 'ACTIVITY',
    key: workKey('ACTIVITY', activity.uuid),
    uuid: activity.uuid,
    title: activity.title,
    subject: activity.subject,
    assignedBy: activity.assignedBy,
    assignedOn: activity.assignedOn,
    dueDate: activity.dueDate,
    typeLabel: ACTIVITY_TYPE_LABEL[activity.activityType],
    // The description says what to make; the instructions say how to hand it
    // in. Both are the child's to read, in that order.
    details: activity.instructions
      ? `${activity.description}\n\nHow to hand it in:\n${activity.instructions}`
      : activity.description,
    maxScore: activity.maxPoints,
    scoreUnit: 'points',
    // Activities are always handed in as a file; a physical hand-in is the
    // teacher's to record.
    submissionFormat: 'FILE',
    attachment: activity.attachment,
    submission: activity.submission && {
      handedInOn: activity.submission.handedInOn,
      score: activity.submission.scoreObtained,
      feedback: activity.submission.feedback,
      fileName: activity.submission.attachmentName,
      repoUrl: null,
    },
    source: { kind: 'ACTIVITY', activity },
  };
}

/**
 * What a hand-in may be, matching the server's `DOCUMENT_TYPES` for the
 * `solution` field — the same list for homework and activities.
 */
export const HAND_IN_ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';

/** The API's `MAX_UPLOAD_MB` default. Checked here only so the child hears it before uploading. */
export const HAND_IN_MAX_MB = 5;
